import React, { ComponentType, useRef } from 'react';
import { Stack } from '@cp/ds/src/components/stack';
import { FormField, __experimentalFormField as FormFieldNext, withChangeableValue, withErrorTooltip } from '@cp/ds/src/components/form';
import { Input, InputTokens } from '@youhelp-ru/ui';
import { useCurrentLanguage } from '@cp/shared/localization';
import { SubmitHandler, useForm } from 'react-hook-form';
import { AppFormProvider, FormContext } from '@cp/utils/form';
import { AddressFieldLocalProvider, CityField, IAddressOption } from '@cp/entities/location';
import { Button } from '@cp/ds/src/components/button';
import { MOBILE_BREAKPOINT_THRESHOLD } from '@cp/ds/src/theme';
import { mergeSx } from '@cp/ds/src/utils/sx/merge';
import { Divider } from '@cp/ds/src/components/divider';
import { useApiServerErrorHandler } from 'src/app/shared/hooks/useApiServerErrorHandler';
import { coreClientProfileApi } from 'src/entities/clientProfile/api';
import { ModeratorMessageField } from 'src/entities/moderation';
import { MODERATOR_MESSAGE_FIELD_NAME_DEFAULT } from 'src/entities/moderation/ui/moderatorMessageField/const';
import { ClientProfileViewModel } from '@common/model/client';
import { ClientType } from '@common/model/client/clientType';
import { SxProps, styled } from '@cp/ds';
import { yupResolver } from '@hookform/resolvers/yup';
import { FormModel, validationSchema } from '../model';

const withoutSize =
  <P extends {}>(Component: ComponentType<P>) =>
  ({ size: _, ...p }: Omit<P, 'size'> & { size?: any }) => <Component {...(p as P)} />;

const InputStylesContext = styled('fieldset', { name: 'InputContext' })(({ theme }) => ({
  [InputTokens.ColorBorder]: theme.palette['gray light'],
  [InputTokens.ColorBorderFocus]: theme.palette.gray,
  [InputTokens.ColorBackground]: theme.palette.background.default,
  [InputTokens.DimensionRadius]: `${theme.borderRadius.normal}px`,
  [InputTokens.ColorTextDisabled]: theme.palette.text.disabled,
  [InputTokens.ColorBorderDisabled]: theme.palette.text.disabled,
  display: 'contents',
  border: 'none',
  margin: 0,
}));
const TextControl = withErrorTooltip(withChangeableValue(withoutSize(Input)));

export interface ClientPersonalInformationFormProps {
  clientProfile: ClientProfileViewModel;
  onComplete?: () => void;
  sendSupportMessage: (params: { clientId: number; message: string; sendCopyBySms?: boolean }) => Promise<void>;
  sx?: SxProps;
}

/**
 * A React functional component that renders a form for managing client personal information.
 *
 * This component utilizes React Hook Form along with Yup resolver for form validation. It also makes use of multiple custom hooks and services for state management, API integration, and messaging functionalities. The form fields include name, surname, birth date, city, address, and a moderator message field.
 *
 * The form submission process includes:
 * - Validation of input using Yup schema.
 * - Updating client profile information through an API request.
 * - Sending a message to the client if provided.
 * - Handling any API errors encountered during the submission.
 *
 * The form consists of the following elements:
 * - Text fields for name and surname.
 * - A date picker for selecting the birth date.
 * - A city search field for selecting the city.
 * - An address field tied to the selected city.
 * - A text field for moderator messages.
 * - A submit button styled for different screen sizes.
 */
export const ClientPersonalInformationForm: React.FC<ClientPersonalInformationFormProps> = ({
  clientProfile,
  onComplete,
  sendSupportMessage,
  sx,
}) => {
  const languageCode = useCurrentLanguage();
  const handleApiServerError = useApiServerErrorHandler();
  // const sendMessageToClient = useSendMessageToClient();
  const [update] = coreClientProfileApi.endpoints.updateClientProfile.useMutation();
  const optionsCache = useRef<IAddressOption[]>([]);

  const form = useForm<FormModel, FormContext>({
    defaultValues: {
      name: clientProfile.name,
      surname: clientProfile.surname,
      birthDate: clientProfile.birthDate,
      location: clientProfile.location,
      cityId: clientProfile.cityId,
      address: clientProfile.address,
    },
    resolver: yupResolver(validationSchema),
    context: {
      languageCode,
    },
  });

  const handleSubmit: SubmitHandler<FormModel> = async (values) => {
    const { [MODERATOR_MESSAGE_FIELD_NAME_DEFAULT]: message, ...restValues } = values;
    const selectedSuggest = optionsCache.current.find((suggestion) => suggestion.label === values.address);
    if (selectedSuggest) {
      restValues.cityFiasId = selectedSuggest.cityFiasId;
      restValues.location = selectedSuggest.location;
    }

    const response = await update({
      clientId: clientProfile.id,
      ...restValues,
    });

    if (response.error) {
      handleApiServerError(response.error, { form });
    } else {
      if (message) {
        await sendSupportMessage({ clientId: clientProfile.id, message: message ?? '' });
      }
      onComplete?.();
    }
  };

  return (
    <AppFormProvider form={form}>
      <Stack
        component="form"
        direction="column"
        gap={2.5}
        noValidate
        onSubmit={form.handleSubmit(handleSubmit)}
        sx={mergeSx({ mx: 'auto', mt: '16px', maxWidth: { xs: 'none', [MOBILE_BREAKPOINT_THRESHOLD]: 415 } }, sx)}
      >
        <InputStylesContext>
          <FormFieldNext Control={TextControl} label="Имя" name="name" />

          {clientProfile.type === ClientType.Specialist && (
            <>
              <FormFieldNext Control={TextControl} label="Фамилия" name="surname" />
              <FormField
                disableFuture
                disablePast={false}
                fieldLabel="Дата рождения"
                name="birthDate"
                pickerType="date"
                placeholder="Дата рождения"
                sx={{ width: 1, maxWidth: 1 }}
                type="dateTimePicker"
                valueDateFormat={false}
              />
            </>
          )}
          <CityField label="Город в каталоге" name="cityId" />
          <AddressFieldLocalProvider
            label="Адрес"
            onOptionsUpdate={(options) => {
              optionsCache.current = options;
            }}
            placeholder="Адрес"
          />
          <Divider />
          <ModeratorMessageField />

          <Button
            color="gray"
            sx={{
              width: { xs: 1, [MOBILE_BREAKPOINT_THRESHOLD]: 'auto' },
              mt: { xs: 2.5, [MOBILE_BREAKPOINT_THRESHOLD]: 2.5 },
            }}
            type="submit"
            variant="primary"
          >
            Cохранить
          </Button>
        </InputStylesContext>
      </Stack>
    </AppFormProvider>
  );
};
